import { SyntheticEvent, useEffect, useState } from 'react';
import { Autocomplete, TextField, Chip } from '@mui/material';
import { TextFieldProps, fieldToTextField } from 'formik-mui';

type CollectionItem = {
  id: number | string;
  label: string;
};

type CollectionMultiSelectProps = {
  items: CollectionItem[];
  isLoading: boolean;
  clearable?: boolean;
  size?: 'small' | 'medium' | undefined;
  customSize?: number;
} & TextFieldProps;

export const FILTER_CHIP_MAX_WIDTH = 200;

export const FilterCollectionMultiSelect = (props: CollectionMultiSelectProps) => {
  const {
    form: { setFieldValue, initialValues, resetForm },
    field: { name },
    size = undefined,
    customSize,
  } = props;

  const { items, isLoading, ...textFieldProps } = props;

  const rawInitialIds = initialValues[name];
  const initialIds = Array.isArray(rawInitialIds) ? rawInitialIds : rawInitialIds ? String(rawInitialIds).split(',') : [];
  const [selectedIds, setSelectedIds] = useState<string[]>(
    initialIds.length ? initialIds.map((id) => String(id)) : [],
  );

  const onChange = (_event: SyntheticEvent<Element, Event>, newValue: CollectionItem[] | null) => {
    const newSelectedIds = newValue ? newValue.map((item) => String(item.id)) : [];
    setSelectedIds(newSelectedIds);
  };

  const [open, setOpen] = useState(false);
  const loading = open && items.length === 0;

  useEffect(() => {
    if (selectedIds.length === 0) {
      resetForm({ values: {} });
    } else {
      setFieldValue(name, selectedIds);
    }
  }, [selectedIds, name, setFieldValue, resetForm]);

  const selectedValues = items.filter((item) => selectedIds.includes(String(item.id)));
  const sliceSize = customSize || 4;

  return (
    <Autocomplete
      fullWidth
      multiple
      value={selectedValues}
      options={items}
      disabled={props.disabled}
      disableClearable
      open={open}
      size={size}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onChange={onChange}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => option.label}
      loading={loading}
      loadingText={'Загрузка...'}
      noOptionsText={'Ничего не найдено'}
      ListboxProps={{
        style: { maxHeight: 'none' },
      }}
      sx={{
        '& .MuiAutocomplete-tag': {
          maxWidth: FILTER_CHIP_MAX_WIDTH,
          minWidth: 0,
        },
        ...(customSize
          ? {
            '& .MuiInputBase-root': {
              flexWrap: 'nowrap !important',
              overflow: 'auto',
            },
          }
          : {}),
      }}
      renderTags={(value, getTagProps) => {
        const displayedValues = value.slice(0, sliceSize);
        const additionalCount = value.length > sliceSize ? value.length - sliceSize : 0;

        const chips = displayedValues.map((chip, index) => {
          const { onDelete: _onDelete, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              {...tagProps}
              key={chip.id}
              size="small"
              variant="outlined"
              label={chip.label}
              sx={{
                maxWidth: FILTER_CHIP_MAX_WIDTH,
                '& .MuiChip-label': {
                  maxWidth: FILTER_CHIP_MAX_WIDTH,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                },
              }}
            />
          );
        });

        if (additionalCount > 0) {
          chips.push(
            <Chip key="more" size="small" variant="outlined" label={`+ ${additionalCount}`} />,
          );
        }

        return chips;
      }}
      renderInput={(params) => (
        <TextField
          {...fieldToTextField(textFieldProps)}
          name={name}
          {...params}
          disabled={props.disabled}
          label={textFieldProps.label}
          placeholder="Выбрать..."
        />
      )}
    />
  );
};
